import { Link } from 'react-router-dom';

interface Comic {
  id: string;
  title: string;
  author: string;
  category: string;
  categoryName: string;
  cover: string;
  updateChapter: string;
  updateTime: string;
  rating: number;
  views: number;
  tags: string[];
  isHot: boolean;
  isNew: boolean;
  isCompleted: boolean;
}

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 韩国漫画网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <p className="text-xs text-white/90 font-medium truncate">{comic.title}</p>
            <p className="text-[10px] text-white/60 mt-0.5">{comic.updateChapter}</p>
          </div>
          {comic.isHot && (
            <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded">
              HOT
            </span>
          )}
          {comic.isNew && (
            <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded">
              NEW
            </span>
          )}
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
        data-product-shop
      >
        <div className="relative w-20 h-28 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 韩国漫画网`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <h4 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
            {comic.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="flex items-center gap-0.5 text-xs text-accent-500">
              <i className="ri-star-fill text-[10px]" />{comic.rating}
            </span>
            <span className="text-[10px] text-foreground-400">{formatViews(comic.views)}阅读</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {comic.tags.map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded bg-background-200 text-foreground-600 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative overflow-hidden rounded-lg bg-background-100 border border-background-200/70 transition-all duration-300 group-hover:border-primary-200">
        <div className="aspect-[3/4] overflow-hidden bg-background-200">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 韩国漫画网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="p-3 md:p-4">
          <h3 className="text-sm md:text-base font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
            {comic.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
          <div className="flex items-center justify-between mt-2.5">
            <span className="flex items-center gap-0.5 text-xs text-accent-500 font-medium">
              <i className="ri-star-fill text-[10px]" />{comic.rating}
            </span>
            <span className="text-[10px] text-foreground-400">{comic.updateChapter}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {comic.tags.map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
        </div>
        {comic.isHot && (
          <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-bold bg-primary-500 text-background-50 rounded-full whitespace-nowrap">
            HOT
          </span>
        )}
        {comic.isNew && (
          <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-bold bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
            NEW
          </span>
        )}
      </div>
    </Link>
  );
}